import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import React, { useEffect, useRef, useState } from 'react';
import { IOption } from '@srm/ui/types/IOption';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { WIICFreeDateService } from '@services/WIICFreeDateService';
import { formatDate } from '@utils/helpers';
import { useForm } from 'react-hook-form';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import Hint from '@srm/ui/hint';
import DateFieldControl from '@srm/ui/controls/DateFieldControl';
import SelectControl from '@srm/ui/controls/SelectControl';
import { IWIICFreeDateProps } from './IWIICFreeDateProps';
import { IWIICFreeDateFormData } from './IWIICFreeDateFormData';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';

export const WIICFreeDateExecutorModal: React.FC<IWIICFreeDateProps> = (props: IWIICFreeDateProps) => {
    const [errorText, setErrorText] = useState<string>();
    const [loading, setLoading] = useState<boolean>();
    const [minDate, setMinDate] = useState<Date | undefined>();
    const [maxDate, setMaxDate] = useState<Date | undefined>();
    const [timeSlots, setTimeSlots] = useState<IOption[]>();
    // Подтверждённая бронь: если отказало сохранение, повтор не бронирует то же время второй раз.
    const reservedAt = useRef<number | undefined>(undefined);
    // Проверка слотов — подготовка: закрытое окно её ответы не применяет.
    const closedRef = useRef(false);
    useEffect(() => {
        closedRef.current = false;
        return () => {
            closedRef.current = true;
        };
    }, []);

    const fields = useTypedSelector((state) => state.document.data?.fields);
    const service = new DocumentExecutionService(props.docId);

    const getTomorrow = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        return tomorrow;
    };

    const getDateFromField = (fieldName: string | undefined) => {
        const field = fields?.find((_) => _.name === fieldName)?.value?.toString();

        return !field ? getTomorrow() : new Date(field);
    };

    const checkFreeDate = (value: Date | null | undefined) => {
        setErrorText('');
        setTimeSlots([]);

        WIICFreeDateService.checkFreeTime(props.docId, value ?? getTomorrow())
            .then((dto) => {
                if (closedRef.current) return;
                const data = dto.data;
                if (data.mindatetime) {
                    setMinDate(new Date(data.mindatetime));
                }
                if (data.maxdatetime) {
                    setMaxDate(new Date(data.maxdatetime));
                }
                if (
                    data.mindatetime &&
                    new Date(data.mindatetime).getTime() > getDateFromField(props.defaultDateField).getTime()
                ) {
                    formState.setValue('freeDate', new Date(data.mindatetime));
                }
                if (!data.datefree) {
                    const date = formatDate(data.newdate!.toString()).date;
                    setErrorText('Выбранная дата недоступна. Ближайший свободный слот ' + date);
                } else {
                    setTimeSlots(
                        data.slot
                            ?.sort((a, b) => a.timeslot!.toString().localeCompare(b.timeslot!.toString()))
                            .map((f) => {
                                const time = formatDate(f.timeslot!.toString()).time;
                                return {
                                    label: time,
                                    value: time,
                                };
                            }),
                    );
                }
            })
            .catch((error) => {
                if (!closedRef.current) setErrorText(error);
            });
    };

    const formState = useForm<IWIICFreeDateFormData>({
        mode: 'onBlur', // "onChange"
        defaultValues: {
            freeDate: getDateFromField(props.defaultDateField),
        },
    });
    const hasUnsavedInput = useUnsavedFormInput(formState);

    useEffect(() => {
        const date = formState.formState.defaultValues?.freeDate;
        checkFreeDate(date);
    }, []);

    const onSubmit = (form: IWIICFreeDateFormData) => {
        // Время обязательно: без выбранного слота резервировать нечего. Ранний выход до
        // setLoading защищает от зависшего busy и обращения к form.freeTime[0] на пустом [].
        if (!form.freeDate || !form.freeTime?.length) return;

        setLoading(true);

        const partDate = form.freeDate;
        const year = partDate.getFullYear();
        const month = partDate.getMonth();
        const day = partDate.getDate();

        const partTime = form.freeTime[0].value.toString().split(':');
        const hour = Number(partTime[0]);
        const minute = Number(partTime[1]);

        const date = new Date(year, month, day, hour, minute);

        const reserve =
            reservedAt.current === date.getTime()
                ? Promise.resolve(true)
                : WIICFreeDateService.reservTime(props.docId, date).then((dto) => {
                      if (dto.data.confirm) reservedAt.current = date.getTime();
                      return Boolean(dto.data.confirm);
                  });

        // Сохранение — часть того же действия: окно ждёт его, иначе закрылось бы с бронью без записи.
        return reserve
            .then((confirmed) => {
                if (!confirmed) {
                    setErrorText('Не пришло подтверждение на выбранное время.');
                    return;
                }
                return service
                    .saveDocumentsBatchEditingData(
                        props.docId,
                        props.actionKey,
                        {
                            fields: [],
                        },
                        undefined,
                    )
                    .then(() => {
                        props.completeHandler && props.completeHandler(true);
                    });
            })
            .catch((error) => setErrorText(error))
            .finally(() => setLoading(false));
    };

    const modalContainerProps: IModalContainerProps = {
        header: props.title,
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: loading,
        closeDisabled: loading,
        hasUnsavedInput,
        onCancel: () => {
            props.completeHandler && props.completeHandler(false);
        },
        //errorText: errorText, // Потребовалось выводить сообщение без префикса "Ошибка: "
        formMethods: formState,
        onSubmit: onSubmit,
    };

    return (
        <ModalContainer {...modalContainerProps}>
            {errorText && <Hint role="alert" title={errorText} tone="danger" maxWidth="100%" />}
            <div style={{ display: 'flex' }}>
                <div style={{ width: '55%' }}>
                    <DateFieldControl
                        name="freeDate"
                        className={'rf-input--left'}
                        required
                        rules={{ required: REQUIRED_MESSAGES.date }}
                        control={formState.control}
                        minDate={minDate ? minDate : getTomorrow()}
                        maxDate={maxDate}
                        readOnly={false}
                        clearable={false}
                        onValueChange={checkFreeDate}
                    />
                </div>
                <div style={{ width: '45%' }} className="rf-input--right">
                    <SelectControl
                        multiselect={false}
                        name="freeTime"
                        options={timeSlots ?? []}
                        loading={loading}
                        required
                        rules={{
                            validate: (value?: IOption[]) =>
                                (Array.isArray(value) && value.length > 0) || REQUIRED_MESSAGES.select,
                        }}
                        control={formState.control}
                        showClearIcon={false}
                        onlySelect={true}
                        getValue={(options: IOption[], value: any) => {
                            if (options.length > 0 && value.length > 0) {
                                return options.filter((x) => x.value === value[0].value);
                            }
                            return [];
                        }}
                        placeholder="Выберите время"
                    />
                </div>
            </div>
        </ModalContainer>
    );
};
